import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { SiteSelector } from '@/components/SiteSelector';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Edit, Trash2, User, CheckCircle, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useSiteRoles } from '@/hooks/useSiteRoles';
import { RoleSelector } from '@/components/RoleSelector';
import { RoleBadgeList } from '@/components/RoleBadge';
import AppHeader from '@/components/AppHeader';

interface Line {
  id: string;
  name: string;
  display_name: string;
  short_display_name: string;
  factory_id: string;
}

interface CoreStation {
  id: string;
  name: string;
  sort_order: number;
  is_active: boolean;
  line_id: string;
}

interface Station {
  id: string;
  line_id: string;
  station_number: number;
  name: string | null;
  required_roles?: string[] | null;
  active: boolean;
  station_claims?: Array<{
    user_id: string;
    profiles: {
      full_name: string;
    };
  }>;
}

const ManageStations = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accessibleLineIds, currentLineId, setCurrentLineId, currentTenantId, allLines } = useAuth();
  const { hasPermission } = useSiteRoles(currentLineId || undefined);
  const [lines, setLines] = useState<Line[]>([]);
  const [stations, setStations] = useState<Station[]>([]);
  const [missingStations, setMissingStations] = useState<CoreStation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [editingStation, setEditingStation] = useState<Station | null>(null);
  const [formData, setFormData] = useState<{
    station_number: string;
    name: string;
    required_roles: string[] | null;
  }>({
    station_number: '',
    name: '',
    required_roles: null,
  });

  const canEdit = hasPermission('mod_styling.site_manager');

  // Check if user has access to the selected site
  const hasAccessToSelectedSite = selectedLineId ? accessibleLineIds.has(selectedLineId) : false;

  useEffect(() => {
    setLines(allLines);
    if (currentLineId && currentLineId !== selectedLineId) {
      setSelectedLineId(currentLineId);
      return;
    }
    if (!selectedLineId && !currentLineId && allLines[0]?.id) {
      setSelectedLineId(allLines[0].id);
    }
  }, [allLines, currentLineId, selectedLineId]);

  const handleLineChange = (lineId?: string) => {
    const nextLineId = lineId || null;
    setSelectedLineId(nextLineId);
    setCurrentLineId(nextLineId);
  };

  useEffect(() => {
    if (selectedLineId && hasAccessToSelectedSite) {
      loadStations(selectedLineId);
    } else if (selectedLineId && !hasAccessToSelectedSite) {
      // Clear stations when viewing a site without access
      setStations([]);
      setLoading(false);
    } else if (!selectedLineId) {
      setStations([]);
      setLoading(false);
    }
  }, [selectedLineId, hasAccessToSelectedSite]);

  const loadStations = async (lineId: string) => {
    if (!currentTenantId) {
      setStations([]);
      setMissingStations([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      console.log('🔍 [ManageStations] Loading stations for line:', lineId);
      const { data, error } = await ((supabase as any)
        .schema('mod_styling'))
        .from('stations')
        .select(`
          *,
          station_claims!left(
            user_id
          )
        `)
        .eq('line_id', lineId)
        .eq('tenant_id', currentTenantId)
        .is('station_claims.released_at', null)
        .order('station_number');

      console.log('🔍 [ManageStations] Stations query result:', {
        data,
        error,
        dataLength: data?.length,
        errorCode: error?.code,
        errorMessage: error?.message,
        sampleStation: data?.[0],
        sampleClaim: data?.[0]?.station_claims?.[0]
      });

      if (error) {
        console.error('❌ [ManageStations] Error loading stations:', error);
        throw error;
      }

      console.log('✅ [ManageStations] Loaded stations:', data?.length);
      const claimUserIds = Array.from(new Set(
        (data || [])
          .flatMap(station => station.station_claims || [])
          .map(claim => claim.user_id)
          .filter(Boolean)
      ));

      let profileMap = new Map<string, string | null>();

      if (claimUserIds.length > 0 && currentTenantId) {
        const { data: profiles } = await ((supabase as any)
          .schema('mod_styling'))
          .from('v_profiles')
          .select('id, full_name')
          .in('id', claimUserIds)
          .eq('tenant_id', currentTenantId);

        profileMap = new Map(
          (profiles || []).map((profile: any) => [profile.id, profile.full_name || null])
        );
      }

      const normalizedStations = (data || []).map(station => ({
        ...station,
        station_claims: (station.station_claims || []).map((claim: any) => ({
          ...claim,
          profiles: {
            full_name: profileMap.get(claim.user_id) || null,
          },
        })),
      }));

      setStations((normalizedStations as unknown as Station[]) || []);

      const { data: coreStations, error: coreError } = await ((supabase as any)
        .schema('core'))
        .from('stations')
        .select('id, name, sort_order, is_active, line_id')
        .eq('tenant_id', currentTenantId)
        .eq('line_id', lineId)
        .order('sort_order');

      if (coreError) {
        console.error('Error loading core stations:', coreError);
        setMissingStations([]);
        return;
      }

      const usedNumbers = new Set(
        normalizedStations.map(station => station.station_number)
      );

      const unmapped = (coreStations || []).filter((station: CoreStation) =>
        !usedNumbers.has(station.sort_order)
      );

      setMissingStations(unmapped);
    } catch (error: any) {
      console.error('💥 [ManageStations] Exception:', error);
      toast.error(t('manageStations.errors.loadStations'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const createEnrichmentForCoreStation = async (coreStation: CoreStation) => {
    if (!canEdit) {
      toast.error(t('manageStations.errors.accessDeniedSave'));
      return;
    }

    if (!currentTenantId || !selectedLineId) {
      toast.error(t('manageStations.errors.missingContext'));
      return;
    }

    const existingNumbers = new Set(stations.map(station => station.station_number));
    const nextNumber = existingNumbers.has(coreStation.sort_order)
      ? Math.max(0, ...Array.from(existingNumbers)) + 1
      : coreStation.sort_order;

    try {
      const { error } = await ((supabase as any)
        .schema('mod_styling'))
        .from('stations')
        .insert({
          tenant_id: currentTenantId,
          line_id: selectedLineId,
          station_number: nextNumber,
          name: coreStation.name,
          active: coreStation.is_active,
        });

        if (error) throw error;

        toast.success(t('manageStations.success.created'));
      loadStations(selectedLineId);
    } catch (error: any) {
      toast.error(error.message || t('manageStations.errors.saveFailed'));
    }
  };

  const handleEditStation = (station: Station) => {
    if (!canEdit) {
      toast.error(t('manageStations.errors.accessDeniedEdit'));
      return;
    }
    setEditingStation(station);
    setFormData({
      station_number: station.station_number.toString(),
      name: station.name || '',
      required_roles: station.required_roles || null,
    });
    setShowDialog(true);
  };

  const handleSaveStation = async () => {
    if (!canEdit) {
      toast.error(t('manageStations.errors.accessDeniedSave'));
      return;
    }
    if (!selectedLineId || !currentTenantId) return;
    const selectedLine = lines.find(line => line.id === selectedLineId);
    if (!selectedLine) {
      toast.error(t('manageStations.errors.missingLineConfig'));
      return;
    }
    if (!formData.station_number) {
      toast.error(t('manageStations.errors.numberRequired'));
      return;
    }

    try {
      const stationNumber = parseInt(formData.station_number);

      if (editingStation) {
        // Update existing station
          const { error } = await ((supabase as any)
            .schema('mod_styling'))
            .from('stations')
            .update({
              station_number: stationNumber,
              name: formData.name || null,
              required_roles: formData.required_roles,
            })
            .eq('id', editingStation.id)
            .eq('tenant_id', currentTenantId);

        if (error) throw error;
        toast.success(t('manageStations.success.updated'));
      } else {
        // Create new station
        const coreName = formData.name?.trim() || `Station ${stationNumber}`;
        const { data: coreStation, error: coreError } = await ((supabase as any)
          .schema('core'))
          .from('stations')
          .insert({
            tenant_id: currentTenantId,
            factory_id: selectedLine.factory_id,
            line_id: selectedLineId,
            name: coreName,
            sort_order: stationNumber,
            is_active: true,
          })
          .select('id')
          .single();

        if (coreError) throw coreError;

        const { error } = await ((supabase as any)
          .schema('mod_styling'))
          .from('stations')
          .insert({
            tenant_id: currentTenantId,
            line_id: selectedLineId,
            station_number: stationNumber,
            name: formData.name || null,
            required_roles: formData.required_roles,
            active: true,
          });

        if (error) throw error;
        toast.success(t('manageStations.success.created'));
      }

      setShowDialog(false);
      loadStations(selectedLineId);
    } catch (error: any) {
      if (error.code === '23505') {
        toast.error(t('manageStations.errors.duplicateNumber'));
      } else {
        toast.error(error.message || t('manageStations.errors.saveFailed'));
      }
    }
  };

  const handleDeleteStation = async (stationId: string) => {
    if (!canEdit) {
      toast.error(t('manageStations.errors.accessDeniedDelete'));
      return;
    }
    if (!confirm(t('manageStations.confirmDelete'))) return;

    try {
      const { error } = await ((supabase as any)
        .schema('mod_styling'))
        .from('stations')
        .delete()
        .eq('id', stationId)
        .eq('tenant_id', currentTenantId);

      if (error) throw error;
      toast.success(t('manageStations.success.deleted'));
      if (selectedLineId) {
        loadStations(selectedLineId);
      }
    } catch (error: any) {
      toast.error(error.message || t('manageStations.errors.deleteFailed'));
    }
  };

  const handleToggleActive = async (station: Station) => {
    if (!canEdit) {
      toast.error(t('manageStations.errors.accessDeniedToggle'));
      return;
    }
    try {
      const { error } = await ((supabase as any)
        .schema('mod_styling'))
        .from('stations')
        .update({ active: !station.active })
        .eq('id', station.id)
        .eq('tenant_id', currentTenantId);

      if (error) throw error;
      toast.success(station.active ? t('manageStations.success.deactivated') : t('manageStations.success.activated'));
      if (selectedLineId) {
        loadStations(selectedLineId);
      }
    } catch (error: any) {
      toast.error(error.message || t('manageStations.errors.updateFailed'));
    }
  };

  if (loading) {
    return <div className="p-8 text-center">{t('manageStations.loading')}</div>;
  }

  const selectedLine = lines.find(line => line.id === selectedLineId);

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary via-background to-muted">
      <AppHeader
        backTo="/"
        backLabel={t('manageStations.backToDashboard')}
        title={t('manageStations.title')}
        rightContent={(
          <SiteSelector
            selectedSiteId={selectedLineId || undefined}
            onSiteChange={handleLineChange}
            showWhenSingle
          />
        )}
      />

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {!selectedLine ? (
            <Card>
              <CardContent className="p-12 text-center text-muted-foreground">
                {t('manageStations.noAccess')}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {missingStations.length > 0 && (
                <Card className="border-dashed">
                  <CardHeader>
                    <CardTitle className="text-base">
                      {t('manageStations.missingMappings')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {missingStations.map((station) => (
                      <div key={station.id} className="flex items-center justify-between gap-4">
                        <div>
                          <div className="text-sm font-medium">{station.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {t('manageStations.sortOrder', { order: station.sort_order })}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => createEnrichmentForCoreStation(station)}
                          disabled={!canEdit}
                        >
                          {t('manageStations.createMapping')}
                        </Button>
                      </div>
                    ))}
                    {!canEdit && (
                      <p className="text-xs text-muted-foreground">
                        {t('manageStations.mappingRequiresRole')}
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader>
                  <div className="flex items-center">
                    <CardTitle className="flex flex-wrap items-center gap-2">
                      <span>{t('manageStations.stationsAt', { siteName: selectedLine.name })}</span>
                      <span className="text-sm font-normal text-muted-foreground">
                        {t('manageStations.totalCount', { count: stations.length })}
                      </span>
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center">
                              <Info className="h-3.5 w-3.5 text-muted-foreground" />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p>
                              {t('manageStations.factoryPilotHintBefore')}{' '}
                              <a
                                href="https://app.factory-pilot.ai"
                                target="_blank"
                                rel="noreferrer"
                                className="font-semibold text-primary underline-offset-2 hover:underline"
                              >
                                {t('manageStations.factoryPilotLink')}
                              </a>
                              {' '}{t('manageStations.factoryPilotHintAfter')}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                {!hasAccessToSelectedSite ? (
                  <div className="text-center py-16">
                    <div className="max-w-md mx-auto space-y-4">
                      <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto">
                        <User className="h-8 w-8 text-muted-foreground" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold mb-2">{t('manageStations.noAccessTitle')}</h3>
                        <p className="text-muted-foreground text-sm">
                          {t('manageStations.noAccessDescription', { siteName: selectedLine.name })}
                        </p>
                        <p className="text-muted-foreground text-xs mt-4">
                          {t('manageStations.noAccessHelp')}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : stations.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    {t('manageStations.noStations')}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('manageStations.tableHeaders.number')}</TableHead>
                            <TableHead>{t('manageStations.tableHeaders.name')}</TableHead>
                            <TableHead>{t('manageStations.tableHeaders.requiredRoles')}</TableHead>
                            <TableHead>{t('manageStations.tableHeaders.status')}</TableHead>
                            <TableHead>{t('manageStations.tableHeaders.currentOperator')}</TableHead>
                            <TableHead className="text-right">{t('manageStations.tableHeaders.actions')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {stations.map(station => {
                            const activeClaim = station.station_claims?.[0];
                            const isOccupied = !!activeClaim;

                            return (
                              <TableRow key={station.id}>
                                <TableCell className="font-semibold">
                                  {station.station_number}
                                </TableCell>
                                <TableCell>
                                  {station.name || <span className="text-muted-foreground">-</span>}
                                </TableCell>
                                <TableCell>
                                  {station.required_roles && station.required_roles.length > 0 ? (
                                    <RoleBadgeList roles={station.required_roles} size="sm" maxDisplay={2} />
                                  ) : (
                                    <span className="text-xs text-muted-foreground">{t('roles.noRolesSelected')}</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <Badge variant={station.active ? 'default' : 'secondary'}>
                                      {station.active ? t('manageStations.status.active') : t('manageStations.status.inactive')}
                                    </Badge>
                                    {isOccupied && (
                                      <Badge variant="outline" className="text-primary border-primary">
                                        <User className="mr-1 h-3 w-3" />
                                        {t('manageStations.status.claimed')}
                                      </Badge>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {isOccupied ? (
                                    <div className="flex items-center gap-2">
                                      <CheckCircle className="h-4 w-4 text-success" />
                                      <span>{activeClaim.profiles?.full_name || t('manageStations.status.unknown')}</span>
                                    </div>
                                  ) : (
                                    <span className="text-muted-foreground">{t('manageStations.status.available')}</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  {canEdit ? (
                                    <div className="flex justify-end gap-2">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleEditStation(station)}
                                      >
                                        <Edit className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleToggleActive(station)}
                                      >
                                        {station.active ? t('manageStations.deactivate') : t('manageStations.activate')}
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDeleteStation(station.id)}
                                        disabled={isOccupied}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">{t('manageStations.viewOnly')}</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                  </Table>
                )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>

      {/* Add/Edit Station Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingStation ? t('manageStations.editStation') : t('manageStations.addNewStation')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="station_number">{t('manageStations.stationNumber')}</Label>
              <Input
                id="station_number"
                type="number"
                placeholder={t('manageStations.stationNumberPlaceholder')}
                value={formData.station_number}
                onChange={(e) => setFormData({ ...formData, station_number: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">{t('manageStations.stationName')}</Label>
              <Input
                id="name"
                type="text"
                placeholder={t('manageStations.stationNamePlaceholder')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            {/* Role selector for stations */}
            <RoleSelector
              selectedRoles={formData.required_roles}
              onChange={(roles) => setFormData({ ...formData, required_roles: roles })}
              label={t('editor.requiredRoles')}
              description={t('manageStations.requiredRolesDescription')}
            />
            {selectedLine && (
              <div className="text-sm text-muted-foreground" dangerouslySetInnerHTML={{ __html: t('manageStations.creatingAt', { siteName: selectedLine.display_name || selectedLine.name }) }} />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              {t('manageStations.cancel')}
            </Button>
            <Button onClick={handleSaveStation}>
              {editingStation ? t('manageStations.update') : t('manageStations.create')} {t('manageStations.station')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageStations;
